import React, { useEffect } from 'react'

/**
 * ChannelAdvisor OAuth Handler
 *
 * ChannelAdvisor doesn't follow their documentation and sends OAuth tokens
 * as URL fragments (#) instead of query parameters (?). Since URL fragments
 * are client-side only and can't be read by the backend, we need this
 * frontend redirect handler to convert fragments to query parameters.
 *
 * Authorization Code Flow (Recommended):
 * 1. ChannelAdvisor redirects to: /integrations/channeladvisor/accept?code=XXX
 * 2. This component detects query parameters and forwards to backend
 * 3. Backend exchanges code for tokens using client secret
 *
 * Implicit Flow (Legacy):
 * 1. ChannelAdvisor redirects to: /integrations/channeladvisor/accept#access_token=XXX&state=XXX
 * 2. This component reads fragments and converts to query parameters
 * 3. Backend processes tokens directly
 */
const ChannelAdvisorFragmentHandler = () => {
  useEffect(() => {
    const getOrigin = () => {
      let origin = window.location.origin
      if (window.location.origin.includes('staging') && window.location.origin.includes('payhelm.com')) {
        origin = 'https://client.staging.payhelm.com'
      } else if (window.location.origin.includes('payhelm.com')) {
        origin = 'https://client.payhelm.com'
      }
      return origin
    }

    // PRIORITY 1: Check for Authorization Code Flow (query parameters)
    const urlParams = new URLSearchParams(window.location.search)
    const authCode = urlParams.get('code')
    const error = urlParams.get('error')
    const errorDescription = urlParams.get('error_description')

    if (authCode || error) {
      console.log('ChannelAdvisor OAuth: Authorization Code Flow detected', {
        hasCode: !!authCode,
        hasError: !!error,
        errorDescription
      })

      if (error) {
        console.error('ChannelAdvisor OAuth Error:', error, errorDescription)
        window.location.href = `/integrations?channeladvisor_error=${error}&description=${encodeURIComponent(errorDescription || '')}`
        return
      }

      // Build the redirect URL to the backend accept route with query parameters
      const backendAcceptUrl = new URL('/auth/channeladvisor/accept', getOrigin())
      backendAcceptUrl.searchParams.append('code', authCode)

      // Preserve any other query parameters
      for (const [key, value] of urlParams) {
        if (key !== 'code' && !backendAcceptUrl.searchParams.has(key)) {
          backendAcceptUrl.searchParams.append(key, value)
        }
      }

      console.log('ChannelAdvisor OAuth: Redirecting to backend (Authorization Code):', backendAcceptUrl.toString())
      window.location.href = backendAcceptUrl.toString()
      return
    }

    // PRIORITY 2: Check for Implicit Flow (URL fragments)
    const fragment = window.location.hash.substring(1) // Remove the # symbol

    if (!fragment) {
      console.error('ChannelAdvisor OAuth: No authorization code or access token found')
      // Redirect to integrations page with error
      window.location.href = '/integrations?channeladvisor_error=no_auth_data'
      return
    }

    console.log('ChannelAdvisor OAuth: Implicit Flow detected - Processing fragment:', fragment)

    // Parse the fragment parameters
    const fragmentParams = new URLSearchParams(fragment)
    const access_token = fragmentParams.get('access_token')
    const token_type = fragmentParams.get('token_type')
    const expires_in = fragmentParams.get('expires_in')
    const fragmentState = fragmentParams.get('state')
    const fragmentError = fragmentParams.get('error')

    // Handle errors in fragment
    if (fragmentError) {
      console.error('ChannelAdvisor OAuth Fragment Error:', fragmentError)
      window.location.href = `/integrations?channeladvisor_error=${fragmentError}`
      return
    }

    // Validate required parameters
    if (!access_token) {
      console.error('ChannelAdvisor OAuth: No access_token in fragment')
      window.location.href = '/integrations?channeladvisor_error=no_access_token'
      return
    }

    // Build the redirect URL to the backend accept route with query parameters
    const backendAcceptUrl = new URL('/auth/channeladvisor/accept', getOrigin())

    // Add all fragment parameters as query parameters
    backendAcceptUrl.searchParams.append('access_token', access_token)
    if (token_type) backendAcceptUrl.searchParams.append('token_type', token_type)
    if (expires_in) backendAcceptUrl.searchParams.append('expires_in', expires_in)
    if (fragmentState) backendAcceptUrl.searchParams.append('state', fragmentState)

    // Preserve any existing query parameters from the original request
    const originalParams = new URLSearchParams(window.location.search)
    for (const [key, value] of originalParams) {
      if (!backendAcceptUrl.searchParams.has(key)) {
        backendAcceptUrl.searchParams.append(key, value)
      }
    }

    console.log('ChannelAdvisor OAuth: Redirecting to backend (Implicit Flow):', backendAcceptUrl.toString())

    // Redirect to the backend accept route with proper query parameters
    window.location.href = backendAcceptUrl.toString()

  }, [])

  return (
    <div className="plain-message pt3 center">
      <h4 className="mt3">
        <span className="fa fa-spinner fa-pulse" style={{ color: '#04a778' }} />&nbsp;
        Connecting your ChannelAdvisor account...
      </h4>
      <p className="text-muted mt2">
        Please wait while we complete the connection process.
      </p>
    </div>
  )
}

export default ChannelAdvisorFragmentHandler
